// v18.0 — Community landing. Clean white-page aesthetic pointing users
// to the CROW producer Discord. Intentionally minimal so the invite
// itself is the hero; no marketing chrome distracts from the join CTA.
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, DiscordLogo } from "@phosphor-icons/react";
import GlobalNav from "../components/GlobalNav";

// v18.1.1 — CrowLogo component deleted. Inlined <img> tags below point
// at the official standing-crow asset.
const CROW_LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/wingman/474b1af8-cac1-436b-9e48-46e676be766d/attachments/050a3ab1b91e401aaab5c54a36a3415b_photo.jpg";

const DISCORD_INVITE_URL = "https://discord.gg/7UjCZqxMm";

export default function CommunityPage() {
  const nav = useNavigate();

  return (
    <div
      className="min-h-screen w-full flex flex-col relative overflow-hidden bg-white text-neutral-900"
      data-testid="community-page"
      // Force light aesthetic regardless of the global crow_theme setting
      // — the community page is intentionally the app's single white
      // room, contrasting the black studio.
      style={{ colorScheme: "light" }}
    >
      {/* Subtle grid texture (light) — mirrors the DAW's dark grid so the
          two surfaces feel like inverses of each other. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Top bar — back link + CROW wordmark */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 py-5">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-2 text-[13px] font-semibold text-neutral-500 hover:text-neutral-900 transition-colors"
          data-testid="community-back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={16} weight="bold" />
          <span>Back</span>
        </button>
        <GlobalNav light />
        <button
          onClick={() => nav("/")}
          className="flex items-center gap-2 text-neutral-900 hover:opacity-80 transition-opacity"
          data-testid="community-logo-home"
          aria-label="CROW home"
        >
          <img
            src={CROW_LOGO_URL}
            alt="CROW"
            style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4 }}
          />
          <span className="font-extrabold tracking-tight text-lg">CROW</span>
        </button>
      </header>

      {/* Hero */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-24">
        <div className="max-w-2xl w-full flex flex-col items-center text-center">
          <div
            className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border border-neutral-200 shadow-[0_10px_40px_rgba(0,0,0,0.06)] flex items-center justify-center mb-8 bg-white"
            data-testid="community-crow-logo"
          >
            <img
              src={CROW_LOGO_URL}
              alt="CROW"
              style={{ width: 54, height: 54, objectFit: "contain", borderRadius: 8 }}
            />
          </div>

          <span
            className="text-[10px] font-mono tracking-[0.3em] text-neutral-400 uppercase mb-4"
            data-testid="community-eyebrow"
          >
            v18.0 · Community
          </span>

          <h1
            className="font-extrabold tracking-tight text-neutral-900 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mb-5"
            data-testid="community-heading"
          >
            Join other CROW members
            <br />
            in this Discord channel.
          </h1>

          <p
            className="text-neutral-500 text-base max-w-xl leading-relaxed mb-10"
            data-testid="community-subhead"
          >
            Share beats, swap kits, and get feedback from producers who ship
            with CROW every day.
          </p>

          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-7 py-4 rounded-full font-extrabold text-base tracking-tight transition-all bg-neutral-900 text-white hover:bg-[#5865F2] shadow-[0_10px_30px_rgba(0,0,0,0.12)] hover:shadow-[0_16px_40px_rgba(88,101,242,0.35)] hover:-translate-y-0.5"
            data-testid="community-discord-cta"
          >
            <DiscordLogo size={22} weight="fill" className="transition-transform group-hover:scale-110" />
            <span>Join the Discord</span>
          </a>

          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 text-[12px] font-mono text-neutral-400 hover:text-neutral-700 transition-colors"
            data-testid="community-discord-url"
          >
            {DISCORD_INVITE_URL.replace("https://", "")}
          </a>
        </div>
      </main>

      <footer className="relative z-10 px-6 md:px-10 pb-6 text-center">
        <p className="text-[10px] font-mono tracking-widest text-neutral-300 uppercase">
          CROW · Composing Recording Original Work
        </p>
      </footer>
    </div>
  );
}
